import { isObject, isString } from 'lodash';
import { nanoid } from 'nanoid';
import React, { useCallback, useMemo, useReducer } from 'react';
import ModalContent from './ModalContent';
import {
  Modal,
  ModalStackActions,
  ModalStackActionsContext,
  ModalStackState,
  ModalStackStateContext,
  NewModalProps,
  PushAlertProps,
  PushConfirmProps,
} from './ModalStackContext';

export const MODAL_STARTING_Z_INDEX = 10;

/**
 * Context wrapper to be used only with class components that provides functions for interacting with the modal stack.
 * @param WrappedComponent Class component to be wrapped. If the component's props has `trackModalStackState` set to `true`,
 *   modal stack state will also be made available.
 * @returns Class component with modal stack context in its props
 */
export function withModalContext<T extends { trackModalStackState?: boolean }>(
  WrappedComponent: React.ComponentType<T>,
) {
  return (props: React.PropsWithChildren<T>) => (
    <ModalStackActionsContext.Consumer>
      {(modalActionsContext) =>
        props.trackModalStackState ? (
          <ModalStackStateContext.Consumer>
            {(modalStateContext) => (
              <WrappedComponent
                {...props}
                modalActions={modalActionsContext}
                modalState={modalStateContext}
              />
            )}
          </ModalStackStateContext.Consumer>
        ) : (
          <WrappedComponent {...props} modalActions={modalActionsContext} />
        )
      }
    </ModalStackActionsContext.Consumer>
  );
}

/**
 * Container to render modals within the modal stack and the modal background
 * @param props Object containing array of existing modals
 * @returns JSX element of each existing modal rendered
 */
function ModalContainer(props: { modals: Modal[] }): JSX.Element {
  const { modals } = props;
  return modals.length > 0 ? (
    <>
      {modals.map((modal) => (
        <React.Fragment key={modal.id}>
          <div style={{ zIndex: modal.index + MODAL_STARTING_Z_INDEX }}>
            {modal.content}
          </div>
        </React.Fragment>
      ))}
    </>
  ) : (
    <></>
  );
}

type Action =
  | { type: 'PUSH_MODAL'; payload: Modal }
  | { type: 'POP_MODAL'; payload: string }
  | { type: 'POP_ALL_MODALS' };

function reducer(modals: Modal[], action: Action): Modal[] {
  switch (action.type) {
    case 'PUSH_MODAL':
      return [...modals, action.payload];
    case 'POP_MODAL':
      return modals.filter((modal) => modal.id !== action.payload);
    case 'POP_ALL_MODALS':
      return [];
    default:
      return modals;
  }
}

/** JSX component to provide centralized modal management via a context provider */
export default function ModalStackProvider({
  children,
}: React.PropsWithChildren<any>): JSX.Element {
  const [modals, dispatch] = useReducer(reducer, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  const pushModal = useCallback(
    (
      newModalProps: NewModalProps,
      popDispatch: (newModalId: string) => void,
    ) => {
      const id = newModalProps.id || `modal_${nanoid()}`;
      const index = modals.length; // New modal will get appended to end of modal array
      const popThisModal = () => popDispatch(id);

      const content = (
        <ModalContent
          id={id}
          content={newModalProps.content}
          className={newModalProps.className}
          index={index}
          popThisModal={popThisModal}
          closeOnBackgroundClick={newModalProps.closeOnBackgroundClick}
        />
      );

      const newModal: Modal = { id, index, content };

      dispatch({ type: 'PUSH_MODAL', payload: newModal });

      return newModal;
    },
    [],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  const popModal = useCallback((modalToPop: Modal | string | undefined) => {
    if (!modalToPop) {
      dispatch({ type: 'POP_MODAL', payload: modals[modals.length - 1].id });
    } else if (isString(modalToPop)) {
      dispatch({ type: 'POP_MODAL', payload: modalToPop });
    } else if (isObject(modalToPop)) {
      dispatch({ type: 'POP_MODAL', payload: modalToPop.id });
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const popAllModals = useCallback(
    () => dispatch({ type: 'POP_ALL_MODALS' }),
    [],
  );

  const pushConfirm = useCallback(
    (props: PushConfirmProps) =>
      pushModal(
        {
          closeOnBackgroundClick: props.closeOnBackgroundClick,
          content: {
            ...props,
            okButton: props.okButton || 'Confirm',
            onOk: () => {
              props.onOk?.();
              props.onClose?.(true);
            },
            onCancel: () => {
              props.onCancel?.();
              props.onClose?.(false);
            },
          },
        },
        (id: string) => dispatch({ type: 'POP_MODAL', payload: id }),
      ),
    [pushModal],
  );

  const pushAlert = useCallback(
    (props: PushAlertProps) =>
      pushModal(
        {
          closeOnBackgroundClick: props.closeOnBackgroundClick,
          content: {
            ...props,
            onOk: () => {
              props.onOk?.();
              props.onClose?.();
            },
            onCancel: () => {
              props.onCancel?.();
              props.onClose?.();
            },
            cancelButton: null,
          },
        },
        (id: string) => dispatch({ type: 'POP_MODAL', payload: id }),
      ),
    [pushModal],
  );

  const modalStackState: ModalStackState = useMemo(
    () => ({
      modals,
      size: modals.length,
      empty: modals.length === 0,
    }),
    [modals],
  );

  const modalStackActions: ModalStackActions = useMemo(
    () => ({
      push: (newModalProps) =>
        pushModal(newModalProps, (newModalId: string) =>
          dispatch({ type: 'POP_MODAL', payload: newModalId }),
        ),
      pop: popModal,
      popAll: popAllModals,
      pushConfirm,
      pushAlert,
    }),
    [pushModal, popModal, popAllModals, pushConfirm, pushAlert],
  );

  return (
    <ModalStackStateContext.Provider value={modalStackState}>
      <ModalStackActionsContext.Provider value={modalStackActions}>
        {children}
        <ModalContainer modals={modals} />
      </ModalStackActionsContext.Provider>
    </ModalStackStateContext.Provider>
  );
}
